import React, { useState, useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import styles from '../../css/components/password-forgotten/passwordForgotten.css';
import { AppContext } from '../../Store';
import TextInput, { TEXT_INPUT_STYLES } from '../forms/TextInput';
import CTAButton from '../buttons/CTAButton';
import ErrorMessage, { ERROR_MESSAGE_STYLES } from '../forms/ErrorMessage';
import { verifyUserIdentifier } from '../../helpers/password-forgotten/verify';
import { DEFAULT_ERROR_CODE } from '../../helpers/server-messages';
import { isEmpty, USERNAME_INPUT_MINIMUM_LENGTH } from '../../helpers/password-forgotten/common';
import { OneLPSsdkParamsContext } from '../../onelps-sdk-params-context';
import { isEmailInvalidFormat } from '../../helpers/validation/common';
import { getEmailFromUrl } from '../../helpers/password-forgotten/reset';

function PasswordForgottenStepOne ({ onSuccessCallback, showSuccessMessageInModal }) {
	const { t, i18n } = useTranslation('translation');
	const [state, dispatch] = useContext(AppContext);
	const [inputValue, setInputValue] = useState('');
	const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
	const [inputValueErrorMessage, setInputValueErrorMessage] = useState('');
	const [isSuccessMessageShownInModal, setIsSuccessMessageShownInModal] = useState(false);
	const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

	const onelpsParams = useContext(OneLPSsdkParamsContext);

	const gameId = onelpsParams.gameId;

	const prefilledEmail = useMemo(() => getEmailFromUrl(), []);

	useEffect(() => {
		if (prefilledEmail) {
			setInputValue(prefilledEmail);
		}
	}, [prefilledEmail]);

	function getMainTextTranslationKey() {
		return i18n.exists(`passwordForgotten.enterPassword.${gameId}.main`) ?
			`passwordForgotten.enterPassword.${gameId}.main` :
			'passwordForgotten.enterPassword.main';
	}

	function getInputLabelText() {
		return i18n.exists(`passwordForgotten.enterPassword.${gameId}.inputLabel`) ?
			t(`passwordForgotten.enterPassword.${gameId}.inputLabel`) :
			t('passwordForgotten.enterPassword.inputLabel');
	}

	function validateFrontend() {
		if (isEmpty(inputValue)) {
			setInputValueErrorMessage(t('passwordForgotten.enterPassword.errorMessages.errorMessageEmpty'));
			setIsErrorMessageShown(true);
			setShowLoadingIndicator(false);

			return false;
		}
		else if (inputValue.length < USERNAME_INPUT_MINIMUM_LENGTH && isEmailInvalidFormat(inputValue)) {
			setInputValueErrorMessage(t('passwordForgotten.enterPassword.errorMessages.errorMessageTooShortV2', { minLength: USERNAME_INPUT_MINIMUM_LENGTH }));
			setIsErrorMessageShown(true);
			setShowLoadingIndicator(false);

			return false;
		}
		return true;
	}

	async function validateBackend() {
		try {
			const result = await verifyUserIdentifier(state.locale, inputValue, dispatch);

			if (result.result === 'ok') {
				setShowLoadingIndicator(false);

				if (showSuccessMessageInModal) {
					setIsSuccessMessageShownInModal(true);
				} else {
					dispatch({ type: 'SET_SERVER_SUCCESS_MESSAGE', payload: t('passwordForgotten.enterPassword.success') });
					dispatch({ type: 'SHOW_SERVER_SUCCESS' });
				}
				onSuccessCallback();
			}

			else if (result.errors.length > 0) {
				setInputValueErrorMessage(t(result.errors[0].code));
				setIsErrorMessageShown(true);
				setIsSuccessMessageShownInModal(false);
				setShowLoadingIndicator(false);
			} else {
				setIsSuccessMessageShownInModal(false);
				setShowLoadingIndicator(false);
			}
		} catch (error) {
			dispatch({ type: 'HIDE_FORGOTPW_OVERLAY' });
			dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
			dispatch({ type: 'SHOW_SERVER_ERROR' });
			setIsSuccessMessageShownInModal(false);
			setShowLoadingIndicator(false);
		}
	}

	if (isSuccessMessageShownInModal) {
		return (
			<div
				className={styles.passwordForgottenWrapper}
				data-testid="successMessageStep1"
			>
				<p>{t('passwordForgotten.enterPassword.success')}</p>
			</div>
		);
	}

	return (
		<form
			className={styles.passwordForgottenWrapper}
			onSubmit={(e) => {
				e.preventDefault();
				setShowLoadingIndicator(true);
				validateFrontend() && validateBackend();
			}}
		>
			<p>
				<Trans i18nKey={`translation:${getMainTextTranslationKey()}`}>
					Please enter your e-mail address. We will send you an e-mail with a link. <br/><br/>If we have not yet received your e-mail address or you have not validated it, we are unable to assist you with recovering your password at this time.
				</Trans>
			</p>
			<fieldset>
				<TextInput
					type="text"
					id="pop-up_pw-forgotten_start-process_input_email"
					labelText={getInputLabelText()}
					value={inputValue}
					onChangeCallback={(e) => setInputValue(e.target.value)}
					style={TEXT_INPUT_STYLES.PASSWORD_FORGOTTEN}
					disabled={showLoadingIndicator}
				/>
				{isErrorMessageShown &&
					<ErrorMessage
						text={inputValueErrorMessage}
						style={ERROR_MESSAGE_STYLES.PASSWORD_FORGOTTEN}
					/>
				}
			</fieldset>

			<CTAButton
				buttonLabel={t('passwordForgotten.enterPassword.buttonLabel')}
				onButtonClick={() => {
					setShowLoadingIndicator(true);
					validateFrontend() && validateBackend();
				}}
				buttonId="pop-up_pw-forgotten_start-process_button_submit"
				extraClassName={'passwordForgotten'}
				isDisabled={showLoadingIndicator}
			/>
		</form>
	);
}

export default PasswordForgottenStepOne;

PasswordForgottenStepOne.propTypes = {
	onSuccessCallback: PropTypes.func.isRequired,
	showSuccessMessageInModal: PropTypes.bool.isRequired,
};
